(function () {
  if (window.__shellCartDrawerBooted) return;
  window.__shellCartDrawerBooted = true;

  const toAjaxCartRoute = (route, fallback) => {
    const value = String(route || fallback || '').trim();
    if (!value) return fallback;
    if (value.includes('.js')) return value;
    const [path, query = ''] = value.split('?');
    const normalizedPath = path.replace(/\/$/, '');
    return `${normalizedPath}.js${query ? `?${query}` : ''}`;
  };

  const cartRoutes = {
    add: toAjaxCartRoute(window.routes && window.routes.cart_add_url, '/cart/add.js'),
    change: toAjaxCartRoute(window.routes && window.routes.cart_change_url, '/cart/change.js'),
    update: toAjaxCartRoute(window.routes && window.routes.cart_update_url, '/cart/update.js'),
    cart: (window.routes && window.routes.cart_url) || '/cart',
  };

  const showToast = (message, type) => {
    const toast = document.querySelector('.cart-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.className = `cart-toast ${type || ''}`.trim();
    requestAnimationFrame(() => toast.classList.add('show'));
    window.setTimeout(() => toast.classList.remove('show'), 2200);
  };

  function parseShopifyNumericId(value) {
    const match = String(value || '').trim().match(/(\d+)(?!.*\d)/);
    return match ? Number(match[1]) || 0 : 0;
  }

  function buildPrivateGiftProperties(config = {}) {
    const level = parseInt(config.level, 10) || 2;
    const properties = {
      _shell_progress_gift: 'true',
      _shell_progress_gift_level: String(level),
      _shell_free_gift: 'true',
    };
    return properties;
  }

  function hasLegacyPublicGiftProperties(item) {
    const properties = (item && item.properties) || {};
    return Boolean(
      properties.ShellProgressGift ||
        properties.ShellProgressGiftLevel ||
        properties.ShellFreeGift ||
        properties._shell_progress_gift_title ||
        properties._shell_progress_gift_tags
    );
  }

  const ShellCartState =
    window.ShellCartState ||
    (() => {
      const storageKey = '__shell_cart_state__';
      const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const api = {
        cart: null,
        lastSyncResult: null,
        emit(type, detail = {}) {
          const entry = {
            type,
            detail,
            time: Date.now(),
          };
          document.dispatchEvent(new CustomEvent('shellcart:event', { detail: entry }));
          return entry;
        },
        setSyncResult(result) {
          this.lastSyncResult = result || null;
          this.emit('gift:sync-result', {
            ok: Boolean(result && result.ok),
            addedGift: Boolean(result && result.addedGift),
            error: result && result.error,
            selectedGift: result && result.selectedGift ? `${result.selectedGift.level}:${result.selectedGift.variantId}` : '',
          });
        },
        updateCart(cart, meta = {}) {
          if (!cart) return;
          this.cart = cart;
          this.emit('cart:update', {
            itemCount: Number(cart.item_count) || 0,
            total: Number(cart.total_price) || 0,
            source: meta.source || '',
          });
          if (!meta.remote) {
            try {
              window.localStorage.setItem(
                storageKey,
                JSON.stringify({
                  tabId,
                  at: Date.now(),
                  itemCount: Number(cart.item_count) || 0,
                  total: Number(cart.total_price) || 0,
                })
              );
            } catch (error) {
              // Storage sync is progressive enhancement.
            }
          }
        },
        storageKey,
        tabId,
      };

      window.addEventListener('storage', (event) => {
        if (event.key !== storageKey || !event.newValue) return;
        let payload = null;
        try {
          payload = JSON.parse(event.newValue);
        } catch (error) {
          return;
        }
        if (!payload || payload.tabId === tabId) return;
        api.emit('cart:remote-tab', { itemCount: payload.itemCount, total: payload.total });
        const drawer = window.CartDrawer || document.querySelector('cart-drawer');
        if (drawer && typeof drawer.refresh === 'function') {
          drawer.refreshPromise = null;
          drawer.refresh({ force: true, giftSyncHandled: true, remote: true });
        }
      });

      return api;
    })();

  window.ShellCartState = ShellCartState;

  class ShellCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.boundOpenTrigger = this.boundOpenTrigger || this.open.bind(this);
      this.refreshPromise = null;
      this.countdownInterval = null;
      this.giftRailTimeout = null;
      this.giftStatusTimeout = null;
      this.isNormalizingCart = false;
      this.liveRenderId = 0;
      this.cartOperationQueue = Promise.resolve();
      this.cartOperationDepth = 0;
      this.lastOptimisticGiftLevel = 0;
      this.pendingGiftCelebrationLevel = 0;
      this.quantityTimers = new Map();
      this.quantityDesired = new Map();
      this.quantityBusyKeys = new Set();
      this.recommendationCache = new Map();
      this.optimisticAddSequence = 0;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeyDown);
      this.bindOpenTriggers();
      this.collapseDuplicateCartItems();
      this.updateBundleTotals();
      window.setTimeout(() => {
        if (typeof window.ShellCartGiftSync === 'function') window.ShellCartGiftSync();
      }, 500);
      window.openCartDrawer = () => this.open();
      window.CartDrawer = this;
      ShellCartState.emit('drawer:ready', { active: this.classList.contains('active') });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeyDown);
      window.clearInterval(this.countdownInterval);
      window.clearTimeout(this.giftStatusTimeout);
    }

    get drawer() {
      return this.querySelector('#CartDrawer');
    }

    get overlay() {
      return this.querySelector('#CartDrawer-Overlay');
    }

    bindOpenTriggers() {
      const triggers = [
        ...document.querySelectorAll('[data-open-cart]'),
        document.querySelector('#cart-icon-bubble'),
        this.querySelector('.cart-trigger-float'),
      ].filter(Boolean);

      triggers.forEach((trigger) => {
        if (trigger.dataset.shellCartBound === 'true') return;
        trigger.dataset.shellCartBound = 'true';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });
    }

    onKeyDown(event) {
      if (event.code === 'Escape') this.close();
    }

    open(triggeredBy, options = {}) {
      if (triggeredBy) this.activeElement = triggeredBy;
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function' && this.drawer) {
        window.setTimeout(() => trapFocus(this.drawer, this.querySelector('.cart-close-btn') || this.drawer), 30);
      }
      this.startCountdown();
      if (!options.skipRefresh) this.refresh();
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    }

    onClick(event) {
      if (event.target.closest('#CartDrawer-Overlay, .cart-close-btn')) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('.qty-btn');
      if (quantityButton) {
        event.preventDefault();
        this.handleQuantityButton(quantityButton);
        return;
      }

      const removeButton = event.target.closest('.cart-item-remove');
      if (removeButton) {
        event.preventDefault();
        const line = parseInt(removeButton.dataset.line, 10);
        const item = this.querySelector(`.cart-item[data-line="${line}"]`);
        this.queueLineQuantity(line, 0, item, 40);
        return;
      }

      const bundleItem = event.target.closest('.bundle-item');
      if (bundleItem && !event.target.closest('.bundle-add-btn')) {
        bundleItem.classList.toggle('selected');
        this.updateBundleTotals();
        return;
      }

      const bundleButton = event.target.closest('.bundle-add-btn');
      if (bundleButton) {
        event.preventDefault();
        this.addBundle(bundleButton);
        return;
      }

      const upsellButton = event.target.closest('.upsell-add-btn');
      if (upsellButton) {
        event.preventDefault();
        this.addUpsell(upsellButton);
        return;
      }

      const navButton = event.target.closest('.swiper-nav-btn');
      if (navButton) {
        event.preventDefault();
        this.scrollUpsells(navButton);
        return;
      }

      if (event.target.closest('.checkout-btn')) {
        event.preventDefault();
        this.goToCheckout();
      }
    }

    async goToCheckout() {
      if (typeof window.ShellCartGiftSync === 'function') {
        this.setGiftSyncStatus(true, 'Updating free gift...');
        try {
          const syncResult = await window.ShellCartGiftSync({ refresh: false });
          if (this.giftSyncChanged(syncResult)) {
            this.refreshPromise = null;
            await this.refresh({ giftSyncHandled: true, force: true });
          }
          this.finishGiftSyncStatus('Free gift ready');
        } catch (error) {
          this.finishGiftSyncStatus('Gift update delayed');
        }
      }

      const discountCode = String((this.drawer && this.drawer.dataset.freeGiftDiscountCode) || '').trim();
      if (discountCode) {
        window.location.href = `/discount/${encodeURIComponent(discountCode)}?redirect=/checkout`;
        return;
      }
      window.location.href = '/checkout';
    }

    handleQuantityButton(button) {
      const line = parseInt(button.dataset.line, 10);
      if (!line) return;
      const item = this.querySelector(`.cart-item[data-line="${line}"]`);
      const display = item && item.querySelector('.qty-display');
      const queued = this.quantityDesired.get(line);
      const currentQuantity =
        queued && Number.isFinite(queued.quantity)
          ? queued.quantity
          : display
            ? parseInt(display.textContent, 10) || 1
            : 1;
      const nextQuantity = button.dataset.action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
      this.queueLineQuantity(line, nextQuantity, item);
    }

    queueLineQuantity(line, quantity, item, delay = 120) {
      if (!line) return;
      const previousGiftLevel = this.getOptimisticGiftLevel(this.getOptimisticPaidTotal());
      const lineKey = item && item.dataset.lineKey;
      const duplicateLineKeys = item && item.dataset.duplicateLineKeys;
      const duplicateLines = item && item.dataset.duplicateLines;

      if (!this.isOptimisticEnabled()) {
        this.updateLine(line, Math.max(0, quantity), { previousGiftLevel, lineKey, duplicateLineKeys, duplicateLines });
        return;
      }
      this.applyOptimisticQuantity(item, quantity);

      window.clearTimeout(this.quantityTimers.get(line));
      this.quantityDesired.set(line, {
        quantity: Math.max(0, quantity),
        previousGiftLevel,
        lineKey,
        duplicateLineKeys,
        duplicateLines,
      });
      this.quantityTimers.set(
        line,
        window.setTimeout(() => {
          const desired = this.quantityDesired.get(line);
          this.quantityTimers.delete(line);
          this.quantityDesired.delete(line);
          if (!desired) return;
          this.updateLine(line, desired.quantity, {
            previousGiftLevel: desired.previousGiftLevel,
            lineKey: desired.lineKey,
            duplicateLineKeys: desired.duplicateLineKeys,
            duplicateLines: desired.duplicateLines,
          });
        }, delay)
      );
    }

    isOptimisticEnabled() {
      return !(this.drawer && this.drawer.dataset.optimisticUpdates === 'false');
    }

    enqueueCartOperation(task) {
      const operationId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const run = this.cartOperationQueue.catch(() => {}).then(async () => {
        this.cartOperationDepth += 1;
        this.classList.add('is-cart-mutating');
        if (this.drawer) this.drawer.setAttribute('aria-busy', 'true');
        ShellCartState.emit('cart:operation-start', {
          id: operationId,
          depth: this.cartOperationDepth,
        });

        try {
          const result = await task();
          ShellCartState.emit('cart:operation-complete', { id: operationId });
          return result;
        } catch (error) {
          ShellCartState.emit('cart:operation-error', {
            id: operationId,
            message: error && error.message ? error.message : String(error),
          });
          throw error;
        } finally {
          this.cartOperationDepth = Math.max(0, this.cartOperationDepth - 1);
          if (!this.cartOperationDepth) {
            this.classList.remove('is-cart-mutating');
            if (this.drawer && !this.drawer.classList.contains('is-gift-syncing-live')) {
              this.drawer.setAttribute('aria-busy', 'false');
            }
          }
        }
      });

      this.cartOperationQueue = run;
      return run;
    }

    async updateLine(line, quantity, options = {}) {
      const busyKey = options.lineKey || String(line || '');
      if (busyKey && this.quantityBusyKeys.has(busyKey)) return;
      if (busyKey) this.quantityBusyKeys.add(busyKey);

      return this.enqueueCartOperation(async () => {
        try {
          const cart = await this.setCartLineQuantity(line, quantity, options);
          await this.refresh({ cart, giftSyncHandled: true, force: true });
        } catch (error) {
          await this.refresh({ force: true });
          showToast('Could not update cart', 'error');
        } finally {
          if (busyKey) this.quantityBusyKeys.delete(busyKey);
        }
      });
    }

    async addItems(items) {
      const response = await fetch(cartRoutes.add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Could not add cart items');
    }

    getDuplicateLineKeys(value) {
      return String(value || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
    }

    async setCartLineQuantity(line, quantity, options = {}) {
      const lineKey = String(options.lineKey || '').trim();
      const duplicateLineKeys = this.getDuplicateLineKeys(options.duplicateLineKeys);

      if (lineKey && duplicateLineKeys.length) {
        const updates = { [lineKey]: Math.max(0, quantity) };
        duplicateLineKeys.forEach((key) => {
          updates[key] = 0;
        });

        try {
          const response = await fetch(cartRoutes.update, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ updates }),
          });
          if (!response.ok) throw new Error('Could not update cart item');
          return response.json();
        } catch (error) {
          const duplicateLines = String(options.duplicateLines || '')
            .split(',')
            .map((value) => parseInt(value, 10))
            .filter(Boolean)
            .sort((a, b) => b - a);

          for (const duplicateLine of duplicateLines) await this.changeLineByIdentifier(duplicateLine, 0);
        }
      }

      const response = await fetch(cartRoutes.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ line, quantity }),
      });
      const cart = response.ok ? await response.json() : null;
      if (!response.ok || !cart) throw new Error('Could not update cart item');
      return cart;
    }

    async addUpsell(button) {
      const variantId = parseInt(button.dataset.variantId, 10);
      if (!variantId) return;
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      try {
        await this.enqueueCartOperation(async () => {
          await this.addItems([{ id: variantId, quantity: 1 }]);
          await this.refresh({ force: true });
        });
        showToast('Added to cart', 'success');
      } catch (error) {
        showToast('Could not add item', 'error');
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    }

    async addBundle(button) {
      const section = button.closest('.cart-bundle-section');
      if (!section) return;
      const selectedItems = [...section.querySelectorAll('.bundle-item.selected')];
      if (!selectedItems.length) {
        showToast('Select bundle items first', 'error');
        return;
      }

      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      try {
        const items = selectedItems
          .map((item) => ({
            id: parseInt(item.dataset.variantId, 10),
            quantity: 1,
          }))
          .filter((item) => item.id);
        if (!items.length) return;
        await this.enqueueCartOperation(async () => {
          await this.addItems(items);
          await this.refresh({ force: true });
        });
        showToast('Bundle added', 'success');
      } catch (error) {
        showToast('Could not add bundle', 'error');
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    }

    scrollUpsells(button) {
      const section = button.closest('.cart-upsell-section');
      const track = section && section.querySelector('.swiper-cart');
      if (!track) return;
      const amount = button.classList.contains('swiper-nav-next') ? 220 : -220;
      track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    updateBundleTotals() {
      this.querySelectorAll('.cart-bundle-section').forEach((section) => {
        const selectedItems = [...section.querySelectorAll('.bundle-item.selected')];
        const total = selectedItems.reduce((sum, item) => sum + (parseInt(item.dataset.price, 10) || 0), 0);
        const totalElement = section.querySelector('.bundle-total span');
        if (totalElement) totalElement.textContent = this.formatMoney(total);
      });
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      return `Rs. ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    getOptimisticPaidTotal() {
      return [...this.querySelectorAll('.cart-item:not(.cart-item--gift)')]
        .filter((item) => !item.hidden && !item.classList.contains('is-merged-duplicate'))
        .reduce((sum, item) => {
          const unitPrice = parseInt(item.dataset.unitPrice || '0', 10) || 0;
          const quantity = parseInt(item.querySelector('.qty-display')?.textContent || '0', 10) || 0;
          return sum + unitPrice * quantity;
        }, 0);
    }

    getOptimisticGiftLevel(total) {
      const progressModule = this.querySelector('[data-progress-module]');
      if (!progressModule) return 0;
      return [1, 2, 3, 4, 5, 6].reduce((levelMatch, level) => {
        const enabled = level === 1 || progressModule.dataset[`giftLevel${level}Enabled`] === 'true';
        let variantId = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Variant`]);
        let threshold = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Threshold`]);
        if (level === 2) {
          variantId = variantId || parseShopifyNumericId(this.drawer && this.drawer.dataset.freeGiftVariantId);
          threshold = threshold || parseShopifyNumericId(this.drawer && this.drawer.dataset.freeGiftThreshold);
        }
        return enabled && variantId && threshold && total >= threshold ? level : levelMatch;
      }, 0);
    }

    getOptimisticGiftConfig(level) {
      const progressModule = this.querySelector('[data-progress-module]');
      if (!progressModule || !level) return null;
      let variantId = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Variant`]);
      if (level === 2) {
        variantId = variantId || parseShopifyNumericId(this.drawer && this.drawer.dataset.freeGiftVariantId);
      }
      return {
        level,
        variantId,
        title: progressModule.dataset[`giftLevel${level}Title`] || `Level ${level} gift`,
        image: progressModule.dataset[`giftLevel${level}Image`] || '',
      };
    }

    getOptimisticItemCount() {
      return [...this.querySelectorAll('.cart-item')]
        .filter((item) => !item.hidden)
        .reduce((count, item) => {
          const display = item.querySelector('.qty-display');
          const quantity = display ? parseInt(display.textContent, 10) || 0 : 1;
          return count + Math.max(0, quantity);
        }, 0);
    }

    updateOptimisticTotals() {
      const total = this.getOptimisticPaidTotal();
      this.querySelectorAll('.cart-subtotal-price').forEach((subtotalNode) => {
        subtotalNode.textContent = this.formatMoney(total);
      });
      this.updateHeaderCounts(this.getOptimisticItemCount());
    }

    hasVisiblePaidItems() {
      return [...this.querySelectorAll('.cart-item:not(.cart-item--gift)')].some((item) => {
        if (item.hidden || item.classList.contains('is-merged-duplicate')) return false;
        const display = item.querySelector('.qty-display');
        const quantity = display ? parseInt(display.textContent, 10) || 0 : 1;
        return quantity > 0;
      });
    }

    renderOptimisticEmptyCart() {
      const body = this.querySelector('.cart-body');
      if (!body) return;

      this.classList.add('is-empty');
      body.innerHTML = `
        <div class="cart-empty cart-empty--live" data-optimistic-empty>
          <div class="cart-empty-icon">Cart</div>
          <h3 class="cart-empty-title">Your cart is empty</h3>
          <p class="cart-empty-sub">Add products to unlock bundles, recommendations and fast checkout.</p>
          <a href="/collections/all" class="cart-empty-cta">Shop now</a>
        </div>
      `;

      this.querySelectorAll('.cart-footer, .cart-offer-strip').forEach((node) => {
        node.hidden = true;
      });
      this.updateHeaderCounts(0);
    }

    applyOptimisticQuantity(item, quantity) {
      if (!item) return;
      const safeQuantity = Math.max(0, quantity);
      item.dataset.optimisticQuantity = String(safeQuantity);
      if (safeQuantity <= 0) item.hidden = true;
      const display = item.querySelector('.qty-display');
      if (display) display.textContent = String(safeQuantity);
      const linePrice = item.querySelector('[data-line-price]');
      const unitPrice = parseInt(item.dataset.unitPrice || '0', 10) || 0;
      if (linePrice) linePrice.textContent = this.formatMoney(unitPrice * safeQuantity);
      this.updateOptimisticTotals();
      if (safeQuantity <= 0 && !this.hasVisiblePaidItems()) {
        this.querySelectorAll('.cart-item--gift').forEach((giftItem) => {
          giftItem.hidden = true;
        });
        this.renderOptimisticEmptyCart();
      }
    }

    animateProgressGiftSync(level = 0) {
      const progressModule = this.querySelector('[data-progress-module]');
      if (!progressModule) return;
      window.clearTimeout(this.giftRailTimeout);
      progressModule.classList.remove('is-gift-syncing');
      progressModule.dataset.syncLevel = String(level || 0);
      void progressModule.offsetHeight;
      progressModule.classList.add('is-gift-syncing');
      this.giftRailTimeout = window.setTimeout(() => {
        progressModule.classList.remove('is-gift-syncing');
      }, 900);
    }

    triggerCartConfetti(level = 0) {
      if (!level || (this.drawer && this.drawer.dataset.rewardConfettiEnabled === 'false')) return;
      const target = this.drawer || this;
      const style = (this.drawer && this.drawer.dataset.rewardConfettiStyle) || 'flowers';
      const density = Math.max(8, Math.min(80, parseInt(this.drawer && this.drawer.dataset.rewardConfettiDensity, 10) || 36));
      let layer = target.querySelector('.cart-confetti-layer');
      if (!layer) {
        layer = document.createElement('div');
        layer.className = 'cart-confetti-layer';
        layer.setAttribute('aria-hidden', 'true');
        target.appendChild(layer);
      }

      layer.className = `cart-confetti-layer cart-confetti-layer--${style}`;
      layer.innerHTML = '';
      for (let index = 0; index < density; index += 1) {
        const petal = document.createElement('span');
        petal.className = `cart-confetti-petal cart-confetti-petal--${style}`;
        petal.style.setProperty('--confetti-left', `${6 + Math.random() * 88}%`);
        petal.style.setProperty('--confetti-delay', `${Math.random() * 0.42}s`);
        petal.style.setProperty('--confetti-size-ratio', `${0.72 + Math.random() * 0.78}`);
        petal.style.setProperty('--confetti-drift', `${-68 + Math.random() * 136}px`);
        petal.style.setProperty('--confetti-hue', `${320 + Math.random() * 70}deg`);
        petal.style.setProperty('--confetti-spin', `${160 + Math.random() * 320}deg`);
        layer.appendChild(petal);
      }

      window.setTimeout(() => {
        if (layer) layer.innerHTML = '';
      }, 3200);
    }

    queueGiftCelebration(level = 0) {
      const safeLevel = parseInt(level, 10) || 0;
      if (!safeLevel) return;
      this.pendingGiftCelebrationLevel = Math.max(this.pendingGiftCelebrationLevel || 0, safeLevel);
    }

    playQueuedGiftCelebration() {
      const level = parseInt(this.pendingGiftCelebrationLevel || '0', 10) || 0;
      if (!level) return;
      this.pendingGiftCelebrationLevel = 0;
      requestAnimationFrame(() => {
        this.animateProgressGiftSync(level);
        this.triggerCartConfetti(level);
      });
    }

    renderGiftSkeleton(config = {}) {
      const itemsSection = this.querySelector('.cart-items-section');
      if (!itemsSection || !config.variantId) return;

      let skeleton = this.querySelector('.cart-item--gift-preview');
      if (!skeleton) {
        skeleton = document.createElement('div');
        skeleton.className = 'cart-item cart-item--gift cart-item--gift-preview is-syncing';
        skeleton.setAttribute('aria-live', 'polite');
        skeleton.innerHTML = `
          <div class="cart-item-image cart-gift-skeleton__image">
            <img class="cart-item-image__media" alt="" loading="eager" width="120" height="144">
            <span class="cart-gift-image-badge cart-gift-image-badge--top-right">FREE</span>
          </div>
          <div class="cart-item-info">
            <p class="cart-item-gift-label">Complimentary reward</p>
            <p class="cart-item-title"></p>
            <div class="cart-gift-skeleton__bar cart-gift-skeleton__bar--wide"></div>
            <div class="cart-item-bottom">
              <div class="cart-item-price">
                <strong class="cart-item-price__free">FREE</strong>
                <span class="compare-price">Adding now</span>
              </div>
              <div class="cart-item-actions"><span class="gift-pill">SYNCING</span></div>
            </div>
          </div>
        `;
        itemsSection.prepend(skeleton);
      }

      skeleton.hidden = false;
      skeleton.dataset.variant = String(config.variantId);
      skeleton.dataset.giftLevel = String(config.level || 2);
      const titleNode = skeleton.querySelector('.cart-item-title');
      const imageNode = skeleton.querySelector('img');
      if (titleNode) titleNode.textContent = config.title || 'Free gift';
      if (imageNode) {
        if (config.image) {
          imageNode.src = config.image;
          imageNode.alt = config.title || 'Free gift';
          imageNode.hidden = false;
        } else {
          imageNode.removeAttribute('src');
          imageNode.alt = '';
          imageNode.hidden = true;
        }
      }
    }

    clearGiftSkeleton() {
      this.querySelectorAll('.cart-item--gift-preview').forEach((placeholder) => {
        placeholder.remove();
      });
    }

    getOptimisticItemsSection() {
      const existingSection = this.querySelector('.cart-items-section');
      if (existingSection) return existingSection;

      const body = this.querySelector('.cart-body');
      if (!body) return null;

      const emptyState = body.querySelector('.cart-empty');
      if (emptyState) emptyState.hidden = true;

      const section = document.createElement('div');
      section.className = 'cart-items-section cart-items-section--optimistic';
      section.dataset.optimisticSection = 'true';
      body.prepend(section);
      return section;
    }

    renderOptimisticAddCard(meta = {}) {
      if (!this.isOptimisticEnabled()) return '';
      const itemsSection = this.getOptimisticItemsSection();
      if (!itemsSection) return '';

      const key = `optimistic-${Date.now()}-${++this.optimisticAddSequence}`;
      const quantity = Math.max(1, parseInt(meta.quantity || '1', 10) || 1);
      const title = meta.title || 'Adding product';
      const variantTitle = meta.variantTitle || '';
      const image = meta.image || '';

      const card = document.createElement('div');
      card.className = 'cart-item cart-item--optimistic-preview is-syncing';
      card.dataset.optimisticCartItem = key;
      card.setAttribute('aria-live', 'polite');
      card.innerHTML = `
        <div class="cart-item-image cart-optimistic-image">
          ${
            image
              ? `<img class="cart-item-image__media" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(title)}" loading="eager" width="120" height="144">`
              : '<span class="cart-optimistic-image__fallback" aria-hidden="true"></span>'
          }
        </div>
        <div class="cart-item-info">
          <p class="cart-item-title">${this.escapeHtml(title)}</p>
          ${variantTitle ? `<p class="cart-item-variant">${this.escapeHtml(variantTitle)}</p>` : ''}
          <div class="cart-optimistic-bars" aria-hidden="true">
            <span></span>
            <span></span>
          </div>
          <div class="cart-item-bottom">
            <div class="cart-item-price">
              <strong>Adding now</strong>
            </div>
            <div class="cart-item-actions">
              <span class="cart-optimistic-qty">Qty ${quantity}</span>
            </div>
          </div>
        </div>
      `;

      itemsSection.prepend(card);
      this.classList.remove('is-empty');
      this.open(null, { skipRefresh: true });

      const currentCount = parseInt(
        document.querySelector('#cart-icon-bubble .cart-count-bubble span:first-child')?.textContent ||
          this.querySelector('.cart-trigger-badge')?.textContent ||
          '0',
        10
      );
      this.updateHeaderCounts((Number.isFinite(currentCount) ? currentCount : 0) + quantity);
      ShellCartState.emit('cart:optimistic-add', { title, quantity });
      return key;
    }

    clearOptimisticAddCards(key) {
      const safeKey = String(key || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const selector = key ? `[data-optimistic-cart-item="${safeKey}"]` : '[data-optimistic-cart-item]';
      this.querySelectorAll(selector).forEach((placeholder) => placeholder.remove());
      const optimisticSection = this.querySelector('.cart-items-section--optimistic[data-optimistic-section="true"]');
      if (optimisticSection && !optimisticSection.children.length) {
        optimisticSection.remove();
        const emptyState = this.querySelector('.cart-empty');
        if (emptyState) emptyState.hidden = false;
      }
    }

    applyOptimisticGift(level) {
      const safeLevel = parseInt(level, 10) || 0;
      this.clearGiftSkeleton();
      if (safeLevel) {
        this.animateProgressGiftSync(safeLevel);
        if (safeLevel > this.lastOptimisticGiftLevel) this.triggerCartConfetti(safeLevel);
      }
      this.lastOptimisticGiftLevel = safeLevel;
      this.updateOptimisticTotals();
    }

    hasGiftConfig() {
      const drawerGiftId = parseShopifyNumericId(this.drawer && this.drawer.dataset.freeGiftVariantId);
      if (drawerGiftId) return true;
      if (this.drawer && this.drawer.dataset.freeGiftProductHandle) return true;
      const progressModule = this.querySelector('[data-progress-module]');
      if (!progressModule) return false;
      return [1, 2, 3, 4, 5, 6].some((level) => {
        const enabled = level === 1 || progressModule.dataset[`giftLevel${level}Enabled`] === 'true';
        const variantId = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Variant`]);
        const productHandle = progressModule.dataset[`giftLevel${level}ProductHandle`] || '';
        return enabled && (variantId || productHandle || (level === 2 && this.drawer && this.drawer.dataset.freeGiftProductHandle));
      });
    }

    setGiftSyncStatus(active, message) {
      const status = this.querySelector('[data-gift-sync-status]');
      this.classList.toggle('is-gift-syncing-live', Boolean(active));
      if (this.drawer) {
        this.drawer.classList.toggle('is-gift-syncing-live', Boolean(active));
        this.drawer.setAttribute('aria-busy', active ? 'true' : 'false');
      }
      if (!status) return;
      const text = status.querySelector('[data-gift-sync-text]');
      if (text && message) text.textContent = message;
      window.clearTimeout(this.giftStatusTimeout);
      status.hidden = !active;
      status.classList.toggle('is-visible', Boolean(active));
      status.classList.remove('is-complete');
    }

    finishGiftSyncStatus(message) {
      const status = this.querySelector('[data-gift-sync-status]');
      this.classList.remove('is-gift-syncing-live');
      if (this.drawer) {
        this.drawer.classList.remove('is-gift-syncing-live');
        this.drawer.setAttribute('aria-busy', 'false');
      }
      if (!status) return;
      const text = status.querySelector('[data-gift-sync-text]');
      if (text && message) text.textContent = message;
      status.hidden = false;
      status.classList.add('is-visible', 'is-complete');
      window.clearTimeout(this.giftStatusTimeout);
      this.giftStatusTimeout = window.setTimeout(() => {
        status.classList.remove('is-visible', 'is-complete');
        status.hidden = true;
      }, 700);
    }

    async fetchCart() {
      const response = await fetch(`${cartRoutes.cart}.js`, {
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      });
      return response.json();
    }

    getMergeableCartLineKey(item) {
      if (!item || !item.key) return '';
      if (this.isProgressGiftItem(item) || item.gift_card || item.selling_plan_allocation) return '';
      return String(item.product_id || item.variant_id || '');
    }

    async changeLineByIdentifier(identifier, quantity) {
      const payload = typeof identifier === 'string' ? { id: identifier, quantity } : { line: identifier, quantity };
      const response = await fetch(cartRoutes.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Could not change cart line');
      return response.json();
    }

    async normalizeDuplicateCartLines(cart) {
      const fallback = { cart, changed: false };
      if (this.isNormalizingCart) return fallback;

      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      const groups = new Map();

      items.forEach((item) => {
        const key = this.getMergeableCartLineKey(item);
        if (!key) return;

        if (!groups.has(key)) {
          groups.set(key, {
            primary: item,
            quantity: 0,
            duplicates: [],
          });
        }

        const group = groups.get(key);
        group.quantity += Number(item.quantity) || 0;
        if (item !== group.primary) group.duplicates.push(item);
      });

      const duplicateGroups = [...groups.values()].filter(
        (group) => group.primary && group.primary.key && group.quantity > 0 && group.duplicates.length > 0
      );

      if (!duplicateGroups.length) return fallback;

      this.isNormalizingCart = true;
      try {
        const updates = {};

        for (const group of duplicateGroups) {
          for (const duplicate of group.duplicates) {
            if (!duplicate.key) continue;
            updates[duplicate.key] = 0;
          }

          updates[group.primary.key] = group.quantity;
        }

        const response = await fetch(cartRoutes.update, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ updates }),
        });
        if (!response.ok) throw new Error('Could not normalize cart lines');
        await response.json();

        return {
          cart: await this.fetchCart(),
          changed: true,
        };
      } catch (error) {
        return fallback;
      } finally {
        this.isNormalizingCart = false;
      }
    }

    async fetchSectionHtml(sectionId) {
      const separator = cartRoutes.cart.includes('?') ? '&' : '?';
      const response = await fetch(`${cartRoutes.cart}${separator}section_id=${sectionId}&_=${Date.now()}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });
      return response.text();
    }

    copyAttributes(target, source) {
      if (!target || !source) return;
      [...target.attributes].forEach((attribute) => target.removeAttribute(attribute.name));
      [...source.attributes].forEach((attribute) => target.setAttribute(attribute.name, attribute.value));
    }

    collapseDuplicateCartItems() {
      const items = [...this.querySelectorAll('.cart-items-section .cart-item')];
      if (!items.length) return;

      const groups = new Map();
      items.forEach((item) => {
        item.hidden = false;
        item.removeAttribute('aria-hidden');
        item.classList.remove('is-merged-duplicate');
        delete item.dataset.duplicateLineKeys;
        delete item.dataset.duplicateLines;

        if (item.classList.contains('cart-item--gift')) return;
        const mergeKey = item.dataset.productId || item.dataset.variant;
        if (!mergeKey) return;
        if (!groups.has(mergeKey)) groups.set(mergeKey, []);
        groups.get(mergeKey).push(item);
      });

      groups.forEach((groupItems) => {
        if (groupItems.length <= 1) return;

        const primary = groupItems[0];
        let totalQuantity = 0;
        let totalLinePrice = 0;
        const duplicateKeys = [];
        const duplicateLines = [];

        groupItems.forEach((item, index) => {
          const quantity = parseInt(item.querySelector('.qty-display')?.textContent || '1', 10) || 1;
          totalQuantity += quantity;
          totalLinePrice += parseInt(item.dataset.linePrice || '0', 10) || 0;

          if (index === 0) return;
          if (item.dataset.lineKey) duplicateKeys.push(item.dataset.lineKey);
          if (item.dataset.line) duplicateLines.push(item.dataset.line);
          item.hidden = true;
          item.classList.add('is-merged-duplicate');
          item.setAttribute('aria-hidden', 'true');
        });

        const display = primary.querySelector('.qty-display');
        if (display) display.textContent = String(totalQuantity);
        const linePrice = primary.querySelector('[data-line-price]');
        const unitPrice = parseInt(primary.dataset.unitPrice || '0', 10) || 0;
        const nextPrice = unitPrice > 0 ? unitPrice * totalQuantity : totalLinePrice;
        if (linePrice) linePrice.textContent = this.formatMoney(nextPrice);
        primary.dataset.liveQuantity = String(totalQuantity);
        primary.dataset.duplicateLineKeys = duplicateKeys.join(',');
        primary.dataset.duplicateLines = duplicateLines.join(',');
      });
    }

    replaceDrawerFromHtml(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sourceElement = parsed.querySelector('cart-drawer');
      if (!sourceElement) return;
      const isActive = this.classList.contains('active');
      this.className = sourceElement.className;
      if (isActive) this.classList.add('active');
      if (this.cartOperationDepth) this.classList.add('is-cart-mutating');
      const sourceDrawer = sourceElement.querySelector('#CartDrawer');
      const targetDrawer = this.drawer;
      const currentBody = targetDrawer && targetDrawer.querySelector('.cart-body');
      const scrollTop = currentBody ? currentBody.scrollTop : 0;
      const discountWasOpen = Boolean(this.querySelector('.cart-discount-details[open]'));

      if (sourceDrawer && targetDrawer) {
        this.copyAttributes(targetDrawer, sourceDrawer);
        targetDrawer.innerHTML = sourceDrawer.innerHTML;
      } else {
        this.innerHTML = sourceElement.innerHTML;
      }

      this.bindOpenTriggers();
      this.collapseDuplicateCartItems();
      this.updateBundleTotals();
      this.restartCountdown();
      this.playQueuedGiftCelebration();
      if (discountWasOpen) {
        const discountDetails = this.querySelector('.cart-discount-details');
        if (discountDetails) discountDetails.open = true;
      }
      requestAnimationFrame(() => {
        const nextBody = this.querySelector('.cart-body');
        if (nextBody) nextBody.scrollTop = Math.min(scrollTop, nextBody.scrollHeight);
      });
    }

    replaceBubbleFromHtml(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sourceBubble = parsed.querySelector('#cart-icon-bubble');
      const targetBubble = document.querySelector('#cart-icon-bubble');
      if (sourceBubble && targetBubble) {
        targetBubble.innerHTML = sourceBubble.innerHTML;
      }
      this.bindOpenTriggers();
    }

    updateHeaderCounts(itemCount) {
      this.querySelectorAll('[data-cart-count], .cart-trigger-badge').forEach((element) => {
        element.textContent = itemCount;
        element.style.display = itemCount > 0 ? '' : 'none';
      });
    }

    dispatchCartState(cart, meta = {}) {
      const detail = {
        cart,
        itemCount: Number(cart && cart.item_count) || 0,
        ...meta,
      };

      ShellCartState.updateCart(cart, meta);
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail,
        })
      );
    }

    getShopRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getAnchorCartItem(cart) {
      const giftVariantId = parseShopifyNumericId(this.drawer && this.drawer.dataset.freeGiftVariantId);
      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      return items.find((item) => item.variant_id !== giftVariantId && !this.isProgressGiftItem(item)) || items[0] || null;
    }

    inferAudience(sourceText) {
      const source = String(sourceText || '').toLowerCase();
      if (source.includes('women') || source.includes('woman') || source.includes('ladies') || source.includes('girl')) return 'women';
      if (source.includes('men') || source.includes('man') || source.includes('boys') || source.includes('boy')) return 'men';
      if (source.includes('kids') || source.includes('kid') || source.includes('baby') || source.includes('child')) return 'kids';
      return 'general';
    }

    inferCategory(sourceText) {
      const source = String(sourceText || '').toLowerCase();
      if (/(shirt|t-shirt|tshirt|tee|kurti|top|blouse|hoodie|sweatshirt|jacket|coat|kurta)/.test(source)) return 'topwear';
      if (/(jean|pant|trouser|bottom|short|palazzo|legging|skirt)/.test(source)) return 'bottomwear';
      if (/(shoe|sneaker|sandal|heel|loafer|slipper|flip flop)/.test(source)) return 'footwear';
      if (/(sunglass|glass|belt|bag|wallet|cap|jewellery|jewelry|earring|necklace|bracelet|ring|watch)/.test(source)) return 'accessory';
      if (/(dress|saree|gown|lehenga)/.test(source)) return 'occasionwear';
      if (/(charger|power bank|powerbank|speaker|earbuds|headphone|laptop|mobile|phone|cable|usb|adapter)/.test(source)) return 'electronics';
      if (/(sofa|bed|table|chair|lamp|kitchen|bottle|mug|cookware|home)/.test(source)) return 'home';
      if (/(lipstick|makeup|serum|cream|beauty|perfume|fragrance|shampoo)/.test(source)) return 'beauty';
      return 'general';
    }

    inferDomain(category) {
      if (['topwear', 'bottomwear', 'footwear', 'accessory', 'occasionwear'].includes(category)) return 'fashion';
      if (category === 'electronics') return 'electronics';
      if (category === 'home') return 'home';
      if (category === 'beauty') return 'beauty';
      return 'general';
    }

    getProductSource(product) {
      return [
        product && (product.title || product.product_title),
        product && (product.type || product.product_type),
        product && product.vendor,
        Array.isArray(product && product.tags) ? product.tags.join(' ') : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    }

    getProductImage(product) {
      if (!product) return '';
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image && product.featured_image.url) return product.featured_image.url;
      if (Array.isArray(product.images) && product.images[0]) {
        return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url || '';
      }
      return '';
    }

    getAvailableVariant(product) {
      if (!product || !Array.isArray(product.variants)) return null;
      return product.variants.find((variant) => variant.available) || product.variants[0] || null;
    }

    async fetchRecommendations(productId, intent, limit) {
      if (!productId) return [];
      const key = `${productId}:${intent}:${limit}`;
      if (this.recommendationCache.has(key)) return this.recommendationCache.get(key);

      const url = `${this.getShopRoot()}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      const request = fetch(url, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) => (Array.isArray(data.products) ? data.products : []))
        .catch(() => []);

      this.recommendationCache.set(key, request);
      return request;
    }

    getAnchorMeta(anchorItem) {
      const source = [
        anchorItem && anchorItem.product_title,
        anchorItem && anchorItem.product_type,
        anchorItem && anchorItem.vendor,
        anchorItem && anchorItem.variant_title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const category = this.inferCategory(source);
      return {
        id: anchorItem && anchorItem.product_id,
        vendor: anchorItem && anchorItem.vendor,
        source,
        category,
        audience: this.inferAudience(source),
        domain: this.inferDomain(category),
      };
    }

    getPreferredBundleCategories(category) {
      if (category === 'topwear') return ['bottomwear', 'footwear', 'accessory'];
      if (category === 'bottomwear') return ['topwear', 'footwear', 'accessory'];
      if (category === 'footwear') return ['topwear', 'bottomwear', 'accessory'];
      if (category === 'accessory') return ['topwear', 'bottomwear', 'footwear'];
      if (category === 'occasionwear') return ['footwear', 'accessory', 'topwear'];
      if (category === 'electronics') return ['electronics'];
      if (category === 'beauty') return ['beauty', 'accessory'];
      return ['general'];
    }

    scoreBundleCandidate(product, anchorMeta, autoMode) {
      const source = this.getProductSource(product);
      const category = this.inferCategory(source);
      const audience = this.inferAudience(source);
      const domain = this.inferDomain(category);
      const preferred = this.getPreferredBundleCategories(anchorMeta.category);
      const preferredIndex = preferred.indexOf(category);

      if (anchorMeta.domain !== 'general' && domain !== 'general' && anchorMeta.domain !== domain) return null;
      if (anchorMeta.domain === 'fashion' && !['fashion', 'general'].includes(domain)) return null;
      if (anchorMeta.domain === 'electronics' && domain !== 'electronics') return null;
      if (autoMode !== 'same_vendor' && anchorMeta.domain === 'fashion' && preferredIndex === -1) return null;

      let score = 0;
      if (preferredIndex >= 0) score += 80 - preferredIndex * 12;
      if (product.vendor && anchorMeta.vendor && product.vendor === anchorMeta.vendor) score += 18;
      if (audience === anchorMeta.audience && audience !== 'general') score += 14;
      if (category === anchorMeta.category) score -= 22;
      if (autoMode === 'same_vendor' && product.vendor === anchorMeta.vendor) score += 28;
      if (autoMode === 'same_vendor' && product.vendor !== anchorMeta.vendor) score -= 12;
      if (autoMode === 'complementary' && preferredIndex >= 0) score += 10;

      return { score, category };
    }

    scoreUpsellCandidate(product, anchorMeta, autoMode) {
      const source = this.getProductSource(product);
      const category = this.inferCategory(source);
      const audience = this.inferAudience(source);
      const domain = this.inferDomain(category);

      if (anchorMeta.domain !== 'general' && domain !== 'general' && anchorMeta.domain !== domain) return null;
      if (anchorMeta.domain === 'fashion' && !['fashion', 'general'].includes(domain)) return null;

      let score = 0;
      if (category === anchorMeta.category) score += 40;
      if (product.vendor && anchorMeta.vendor && product.vendor === anchorMeta.vendor) score += 16;
      if (audience === anchorMeta.audience && audience !== 'general') score += 14;

      if (autoMode === 'same_style') {
        if (category !== anchorMeta.category) return null;
        score += 30;
      } else if (autoMode === 'same_vendor') {
        if (product.vendor !== anchorMeta.vendor) score -= 12;
        else score += 30;
      } else {
        if (category === anchorMeta.category) score += 18;
      }

      return { score, category };
    }

    buildBundleItemMarkup(product, section) {
      const variant = this.getAvailableVariant(product);
      if (!variant) return '';
      const showVendor = section.dataset.showVendor === 'true';
      const showComparePrice = section.dataset.showComparePrice === 'true';
      const imageUrl = this.getProductImage(product);
      const comparePrice = variant.compare_at_price || 0;

      return `
        <div class="bundle-item selected" data-variant-id="${variant.id}" data-price="${variant.price || 0}">
          <div class="bundle-item-checkbox"></div>
          <div class="bundle-item-img">
            ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>
          <div class="bundle-item-info">
            ${showVendor && product.vendor ? `<p class="bundle-item-vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
            <p class="bundle-item-name">${this.escapeHtml(product.title)}</p>
            <p class="bundle-item-price">
              ${this.formatMoney(variant.price || 0)}
              ${showComparePrice && comparePrice > (variant.price || 0) ? `<span>${this.formatMoney(comparePrice)}</span>` : ''}
            </p>
          </div>
        </div>
      `;
    }

    buildUpsellItemMarkup(product, section) {
      const variant = this.getAvailableVariant(product);
      if (!variant) return '';
      const showVendor = section.dataset.showVendor === 'true';
      const showComparePrice = section.dataset.showComparePrice === 'true';
      const imageUrl = this.getProductImage(product);
      const comparePrice = variant.compare_at_price || 0;
      const buttonLabel = section.dataset.buttonLabel || 'Add to cart';

      return `
        <div class="upsell-card">
          <div class="upsell-card-img">
            ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>
          <div class="upsell-card-body">
            ${showVendor && product.vendor ? `<p class="upsell-card-vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
            <p class="upsell-card-title">${this.escapeHtml(product.title)}</p>
            <div class="upsell-card-price-row">
              <span class="upsell-card-price">${this.formatMoney(variant.price || 0)}</span>
              ${showComparePrice && comparePrice > (variant.price || 0) ? `<span class="upsell-card-compare">${this.formatMoney(comparePrice)}</span>` : ''}
            </div>
            <button class="upsell-add-btn" type="button" data-variant-id="${variant.id}">${this.escapeHtml(buttonLabel)}</button>
          </div>
        </div>
      `;
    }

    async hydrateBundleSections(cart) {
      const sections = [...this.querySelectorAll('.cart-bundle-section[data-source-mode="auto"]')];
      if (!sections.length) return;

      const anchorItem = this.getAnchorCartItem(cart);
      const anchorMeta = this.getAnchorMeta(anchorItem);
      if (!anchorMeta.id) {
        sections.forEach((section) => {
          section.hidden = true;
        });
        return;
      }

      const cartProductIds = new Set((cart.items || []).map((item) => item.product_id));
      const [complementary, related] = await Promise.all([
        this.fetchRecommendations(anchorMeta.id, 'complementary', 16),
        this.fetchRecommendations(anchorMeta.id, 'related', 16),
      ]);

      sections.forEach((section) => {
        const autoMode = section.dataset.autoMode || 'complete_look';
        const limit = parseInt(section.dataset.limit || '3', 10) || 3;
        const itemsTarget = section.querySelector('[data-smart-items]');
        const addButton = section.querySelector('.bundle-add-btn');
        if (!itemsTarget) return;

        const scored = [...complementary, ...related]
          .filter((product, index, array) => product && array.findIndex((candidate) => candidate && candidate.id === product.id) === index)
          .filter((product) => !cartProductIds.has(product.id))
          .map((product) => {
            const result = this.scoreBundleCandidate(product, anchorMeta, autoMode);
            return result ? { product, score: result.score } : null;
          })
          .filter(Boolean)
          .sort((a, b) => b.score - a.score)
          .slice(0, limit);

        if (!scored.length) {
          section.hidden = true;
          return;
        }

        section.hidden = false;
        itemsTarget.innerHTML = scored.map((entry) => this.buildBundleItemMarkup(entry.product, section)).join('');
        if (addButton) {
          addButton.disabled = false;
          addButton.textContent = section.dataset.buttonLabel || 'Add bundle';
        }
      });

      this.updateBundleTotals();
    }

    async hydrateUpsellSections(cart) {
      const sections = [...this.querySelectorAll('.cart-upsell-section[data-source-mode="auto"]')];
      if (!sections.length) return;

      const anchorItem = this.getAnchorCartItem(cart);
      const anchorMeta = this.getAnchorMeta(anchorItem);
      if (!anchorMeta.id) {
        sections.forEach((section) => {
          section.hidden = true;
        });
        return;
      }

      const cartProductIds = new Set((cart.items || []).map((item) => item.product_id));
      const [related, complementary] = await Promise.all([
        this.fetchRecommendations(anchorMeta.id, 'related', 18),
        this.fetchRecommendations(anchorMeta.id, 'complementary', 18),
      ]);

      sections.forEach((section) => {
        const autoMode = section.dataset.autoMode || 'same_style';
        const limit = parseInt(section.dataset.limit || '4', 10) || 4;
        const track = section.querySelector('[data-smart-items]');
        if (!track) return;

        const scored = [...related, ...complementary]
          .filter((product, index, array) => product && array.findIndex((candidate) => candidate && candidate.id === product.id) === index)
          .filter((product) => !cartProductIds.has(product.id))
          .map((product) => {
            const result = this.scoreUpsellCandidate(product, anchorMeta, autoMode);
            return result ? { product, score: result.score } : null;
          })
          .filter(Boolean)
          .sort((a, b) => b.score - a.score)
          .slice(0, limit);

        if (!scored.length) {
          section.hidden = true;
          return;
        }

        section.hidden = false;
        track.innerHTML = scored.map((entry) => this.buildUpsellItemMarkup(entry.product, section)).join('');
      });
    }

    async hydrateSmartSections(cart) {
      await Promise.all([this.hydrateBundleSections(cart), this.hydrateUpsellSections(cart)]);
    }

    isProgressGiftItem(item) {
      const properties = (item && item.properties) || {};
      return (
        properties._shell_progress_gift === 'true' ||
        properties._shell_progress_gift_level ||
        properties.ShellProgressGift === 'true' ||
        properties.ShellProgressGiftLevel ||
        properties._shell_free_gift === 'true' ||
        properties.ShellFreeGift === 'true'
      );
    }

    giftSyncChanged(syncResult) {
      if (!syncResult || syncResult.ok === false) return false;
      return Boolean(
        syncResult.addedGift ||
          (Array.isArray(syncResult.lineUpdates) && syncResult.lineUpdates.length) ||
          (Array.isArray(syncResult.removals) && syncResult.removals.length)
      );
    }

    async prepareLiveCart(cart) {
      let liveCart = cart || (await this.fetchCart());
      const normalizeResult = await this.normalizeDuplicateCartLines(liveCart);
      liveCart = normalizeResult.cart || liveCart;

      const syncResult =
        typeof window.ShellCartGiftSync === 'function'
          ? await window.ShellCartGiftSync({ cart: liveCart, refresh: false })
          : null;

      if (this.giftSyncChanged(syncResult)) {
        liveCart = await this.fetchCart();
        const postGiftNormalize = await this.normalizeDuplicateCartLines(liveCart);
        liveCart = postGiftNormalize.cart || liveCart;
      }

      return {
        cart: liveCart,
        changed: Boolean(normalizeResult.changed || this.giftSyncChanged(syncResult)),
        syncResult,
      };
    }

    async refresh(options = {}) {
      if (this.refreshPromise && !options.cart && !options.force) return this.refreshPromise;

      const renderId = ++this.liveRenderId;
      const refreshPromise = Promise.resolve()
        .then(async () => {
          const { cart, syncResult } = await this.prepareLiveCart(options.cart);
          const [drawerHtml, bubbleHtml] = await Promise.all([
            this.fetchSectionHtml('cart-drawer'),
            this.fetchSectionHtml('cart-icon-bubble'),
          ]);
          if (renderId !== this.liveRenderId) return cart;
          this.replaceDrawerFromHtml(drawerHtml);
          this.replaceBubbleFromHtml(bubbleHtml);
          this.updateHeaderCounts(cart.item_count);
          if (!options.giftSyncHandled && typeof window.ShellCartGiftSync === 'function') {
            window.setTimeout(() => window.ShellCartGiftSync({ cart, refresh: true }), 0);
          }
          this.dispatchCartState(cart, {
            source: options.remote ? 'remote-refresh' : 'drawer-refresh',
            remote: Boolean(options.remote),
            giftSyncHandled: options.giftSyncHandled !== false,
            syncResult,
          });
          await this.hydrateSmartSections(cart);
          return cart;
        })
        .catch(() => {
          showToast('Could not refresh cart', 'error');
        })
        .finally(() => {
          if (this.refreshPromise === refreshPromise) this.refreshPromise = null;
        });

      this.refreshPromise = refreshPromise;
      return this.refreshPromise;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: 'cart-drawer',
        },
        {
          id: 'cart-icon-bubble',
          selector: '#cart-icon-bubble',
        },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      if (parsedState.sections && parsedState.sections['cart-drawer']) {
        this.replaceDrawerFromHtml(parsedState.sections['cart-drawer']);
      }
      if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
        this.replaceBubbleFromHtml(parsedState.sections['cart-icon-bubble']);
      }
      this.classList.remove('is-empty');
      if (Number.isFinite(Number(parsedState.item_count))) {
        this.updateHeaderCounts(Number(parsedState.item_count));
      }
      ShellCartState.emit('cart:add-render', {
        itemCount: Number.isFinite(Number(parsedState.item_count)) ? Number(parsedState.item_count) : null,
      });
      this.open(null, { skipRefresh: true });
      if (this.hasGiftConfig()) this.setGiftSyncStatus(true, 'Updating free gift...');
      this.refreshPromise = null;
      window.setTimeout(() => {
        this.refresh({ force: true, giftSyncHandled: true });
      }, 0);
    }

    startCountdown() {
      const timer = this.querySelector('.countdown-timer');
      if (!timer) return;
      const secondsDefault = parseInt(this.drawer && this.drawer.dataset.countdownSeconds, 10) || 600;
      let seconds = parseInt(sessionStorage.getItem('shell_cart_countdown'), 10);
      if (!Number.isFinite(seconds) || seconds <= 0) seconds = secondsDefault;

      window.clearInterval(this.countdownInterval);
      const tick = () => {
        const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
        const remSeconds = String(seconds % 60).padStart(2, '0');
        const freshTimer = this.querySelector('.countdown-timer');
        if (freshTimer) freshTimer.textContent = `${minutes}:${remSeconds}`;
        sessionStorage.setItem('shell_cart_countdown', String(seconds));
        seconds = seconds <= 0 ? secondsDefault : seconds - 1;
      };

      tick();
      this.countdownInterval = window.setInterval(tick, 1000);
    }

    restartCountdown() {
      if (this.classList.contains('active')) this.startCountdown();
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', ShellCartDrawer);
  }

  const isProgressGiftLine = (item) => {
    const properties = (item && item.properties) || {};
    return (
      properties._shell_progress_gift === 'true' ||
      properties._shell_progress_gift_level ||
      properties.ShellProgressGift === 'true' ||
      properties.ShellProgressGiftLevel ||
      properties._shell_free_gift === 'true' ||
      properties.ShellFreeGift === 'true'
    );
  };

  const progressGiftProductCache = new Map();
  const failedGiftVariantIds = new Set();

  const getShopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function normalizeProductHandle(handle) {
    return String(handle || '')
      .replace(/^\/?products\//, '')
      .split('?')[0]
      .trim();
  }

  async function resolveGiftProduct(handle) {
    const cleanHandle = normalizeProductHandle(handle);
    if (!cleanHandle) return null;
    if (progressGiftProductCache.has(cleanHandle)) return progressGiftProductCache.get(cleanHandle);

    const request = fetch(`${getShopRoot()}products/${encodeURIComponent(cleanHandle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    progressGiftProductCache.set(cleanHandle, request);
    return request;
  }

  async function resolveGiftVariantId(config) {
    if (!config || (config.variantId && !failedGiftVariantIds.has(String(config.variantId)))) return config;
    const product = await resolveGiftProduct(config.productHandle);
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    const variant =
      variants.find((entry) => entry && entry.available !== false && !failedGiftVariantIds.has(String(entry.id))) ||
      variants.find((entry) => entry && entry.available !== false) ||
      variants[0];
    if (!variant || !variant.id) return config;

    return {
      ...config,
      variantId: parseShopifyNumericId(variant.id),
      title: config.title || product.title || 'Free Gift',
      image:
        config.image ||
        (product.featured_image && (product.featured_image.src || product.featured_image)) ||
        (Array.isArray(product.images) ? product.images[0] : '') ||
      '',
    };
  }

  async function addGiftItem(config) {
    const buildPayload = (variantId, singleItem = false) => {
      const item = {
        id: variantId,
        quantity: 1,
        properties: buildPrivateGiftProperties(config),
      };
      return singleItem ? item : { items: [item] };
    };

    const send = (variantId, singleItem = false) =>
      fetch(cartRoutes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        cache: 'no-store',
        body: JSON.stringify(buildPayload(variantId, singleItem)),
      });

    let response = await send(config.variantId);
    if (!response.ok) response = await send(config.variantId, true);
    if (response.ok) return { response, variantId: config.variantId, retried: false };

    failedGiftVariantIds.add(String(config.variantId));
    const fallbackConfig = await resolveGiftVariantId({ ...config, variantId: 0 });
    if (!fallbackConfig || !fallbackConfig.variantId || fallbackConfig.variantId === config.variantId) {
      return { response, variantId: config.variantId, retried: false };
    }

    response = await send(fallbackConfig.variantId);
    if (!response.ok) response = await send(fallbackConfig.variantId, true);
    return {
      response,
      variantId: fallbackConfig.variantId,
      retried: true,
      fallbackConfig,
    };
  }

  function getDrawerFreeGiftConfig(drawer) {
    const variantId = parseShopifyNumericId(drawer && drawer.dataset.freeGiftVariantId);
    const productHandle = (drawer && drawer.dataset.freeGiftProductHandle) || '';
    const threshold = parseShopifyNumericId(drawer && drawer.dataset.freeGiftThreshold);
    if ((!variantId && !productHandle) || !threshold) return null;

    return {
      level: 2,
      enabled: true,
      variantId,
      productHandle,
      threshold,
      title: (drawer && drawer.dataset.freeGiftTitle) || 'Free Gift',
      image: (drawer && drawer.dataset.freeGiftImage) || '',
      requiredTags: [],
    };
  }

  function getLiveCartDrawer() {
    return window.CartDrawer || document.querySelector('cart-drawer');
  }

  function giftSyncChangedGlobally(syncResult) {
    if (!syncResult || syncResult.ok === false) return false;
    return Boolean(
      syncResult.addedGift ||
        (Array.isArray(syncResult.lineUpdates) && syncResult.lineUpdates.length) ||
        (Array.isArray(syncResult.removals) && syncResult.removals.length)
    );
  }

  function rememberGiftSyncResult(result) {
    window.__shellLastGiftSyncResult = result;
    if (window.ShellCartState && typeof window.ShellCartState.setSyncResult === 'function') {
      window.ShellCartState.setSyncResult(result);
    }
    return result;
  }

  async function syncProgressGiftsGlobally(options = {}) {
    if (window.__shellProgressGiftSyncing) {
      let waited = 0;
      while (window.__shellProgressGiftSyncing && waited < 1200) {
        await new Promise((resolve) => window.setTimeout(resolve, 60));
        waited += 60;
      }
      if (window.__shellProgressGiftSyncing) return window.__shellLastGiftSyncResult || null;
    }
    const shouldRefresh = options.refresh !== false;

    const drawer = document.querySelector('#CartDrawer');
    const progressModule = document.querySelector('cart-drawer [data-progress-module], [data-progress-module]');
    let configs = [];

    if (progressModule) {
      configs = [1, 2, 3, 4, 5, 6]
        .map((level) => {
          const enabled = level === 1 || progressModule.dataset[`giftLevel${level}Enabled`] === 'true';
          let variantId = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Variant`]);
          let productHandle = progressModule.dataset[`giftLevel${level}ProductHandle`] || '';
          let threshold = parseShopifyNumericId(progressModule.dataset[`giftLevel${level}Threshold`]);
          if (level === 2) {
            variantId = variantId || parseShopifyNumericId(drawer && drawer.dataset.freeGiftVariantId);
            productHandle = productHandle || (drawer && drawer.dataset.freeGiftProductHandle) || '';
            threshold = threshold || parseShopifyNumericId(drawer && drawer.dataset.freeGiftThreshold);
          }
          const requiredTags = String(progressModule.dataset[`giftLevel${level}Tags`] || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);

          return {
            level,
            enabled,
            variantId,
            productHandle,
            threshold,
            title: progressModule.dataset[`giftLevel${level}Title`] || `Level ${level} gift`,
            image: progressModule.dataset[`giftLevel${level}Image`] || '',
            requiredTags: [],
            giftTags: requiredTags,
          };
        })
        .filter((config) => config.enabled && (config.variantId || config.productHandle) && config.threshold);
    }

    if (!configs.length) {
      const fallbackConfig = getDrawerFreeGiftConfig(drawer);
      if (fallbackConfig) {
        configs = [fallbackConfig];
      }
    }

    configs = (await Promise.all(configs.map((config) => resolveGiftVariantId(config)))).filter(
      (config) => config.enabled && config.variantId && config.threshold
    );

    if (!configs.length) {
      const fallbackConfig = await resolveGiftVariantId(getDrawerFreeGiftConfig(drawer));
      if (fallbackConfig && fallbackConfig.variantId && fallbackConfig.threshold) {
        configs = [fallbackConfig];
      }
    }

    if (!configs.length) {
      const attempt = Number(options.attempt || 0);
      const reason = progressModule ? 'no_gift_configs' : 'missing_progress_module';
      rememberGiftSyncResult({ ok: false, reason, attempt, configs });
      if (!progressModule && options.retry !== false && attempt < 6) {
        window.setTimeout(() => syncProgressGiftsGlobally({ ...options, attempt: attempt + 1 }), 120);
      }
      return window.__shellLastGiftSyncResult;
    }

    window.__shellProgressGiftSyncing = true;
    try {
      const optionCart = options.cart && Array.isArray(options.cart.items) ? options.cart : null;
      const cart =
        optionCart ||
        (await fetch(`${cartRoutes.cart}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) =>
          response.json()
      ));
      const paidItems = (cart.items || []).filter((item) => !isProgressGiftLine(item));
      const paidTotal = paidItems.reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);

      if (!paidItems.length) {
        const removals = (cart.items || [])
          .map((item, index) => (isProgressGiftLine(item) ? { line: index + 1, quantity: 0 } : null))
          .filter(Boolean)
          .sort((a, b) => b.line - a.line);
        const drawerElement = getLiveCartDrawer();
        if (removals.length && drawerElement && typeof drawerElement.setGiftSyncStatus === 'function') {
          drawerElement.setGiftSyncStatus(true, 'Removing free gift...');
        }
        for (const removal of removals) {
          await fetch(cartRoutes.change, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(removal),
          });
        }
        if (shouldRefresh && removals.length && window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
          window.CartDrawer.refreshPromise = null;
          window.CartDrawer.refresh({ giftSyncHandled: true, force: true });
        }
        if (removals.length && drawerElement && typeof drawerElement.finishGiftSyncStatus === 'function') {
          drawerElement.finishGiftSyncStatus('Free gift removed');
        }
        return rememberGiftSyncResult({ ok: true, paidTotal: 0, configs, selectedGift: null, removals });
      }

      const qualifiedGifts = configs.filter((config) => paidTotal >= config.threshold).sort((a, b) => b.level - a.level);
      let selectedGift = qualifiedGifts[0] || null;

      const selectedKey = selectedGift ? `${selectedGift.level}:${selectedGift.variantId}` : '';
      const lineUpdates = [];
      let selectedGiftExists = false;

      (cart.items || []).forEach((item, index) => {
        if (!isProgressGiftLine(item)) return;
        const properties = item.properties || {};
        const level =
          properties._shell_progress_gift_level ||
          properties.ShellProgressGiftLevel ||
          (properties._shell_free_gift === 'true' || properties.ShellFreeGift === 'true' ? '2' : '');
        const key = `${level}:${item.variant_id}`;
        if (key === selectedKey) {
          if (hasLegacyPublicGiftProperties(item)) {
            lineUpdates.push({ line: index + 1, quantity: 0 });
          } else {
            selectedGiftExists = true;
            if (item.quantity !== 1) lineUpdates.push({ line: index + 1, quantity: 1 });
          }
        } else {
          lineUpdates.push({ line: index + 1, quantity: 0 });
        }
      });

      const orderedLineUpdates = lineUpdates
        .filter((update, index, array) => array.findIndex((entry) => entry.line === update.line) === index)
        .sort((a, b) => b.line - a.line);

      const giftWillChange = Boolean((selectedGift && !selectedGiftExists) || orderedLineUpdates.length);
      const drawerElement = getLiveCartDrawer();
      if (giftWillChange && drawerElement && typeof drawerElement.setGiftSyncStatus === 'function') {
        drawerElement.setGiftSyncStatus(true, selectedGift ? 'Adding free gift...' : 'Updating free gift...');
      }
      if (selectedGift && !selectedGiftExists && drawerElement && typeof drawerElement.renderGiftSkeleton === 'function') {
        drawerElement.renderGiftSkeleton(selectedGift);
      }

      for (const update of orderedLineUpdates) {
        await fetch(cartRoutes.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(update),
        });
      }

      if (selectedGift && !selectedGiftExists) {
        let giftAdd = await addGiftItem(selectedGift);
        if (!giftAdd.response.ok) {
          const nextGift = qualifiedGifts.find(
            (config) => config !== selectedGift && !failedGiftVariantIds.has(String(config.variantId))
          );
          if (nextGift) {
            selectedGift = nextGift;
            giftAdd = await addGiftItem(selectedGift);
          }
        }
        if (!giftAdd.response.ok) throw new Error(`Gift add failed: ${giftAdd.response.status}`);
        if (giftAdd.variantId !== selectedGift.variantId) {
          selectedGift.variantId = giftAdd.variantId;
          if (giftAdd.fallbackConfig) {
            selectedGift.title = giftAdd.fallbackConfig.title || selectedGift.title;
            selectedGift.image = giftAdd.fallbackConfig.image || selectedGift.image;
          }
        }
      }

      if (selectedGift && !selectedGiftExists && drawerElement && typeof drawerElement.queueGiftCelebration === 'function') {
        drawerElement.queueGiftCelebration(selectedGift.level);
      } else if (giftWillChange && drawerElement && typeof drawerElement.animateProgressGiftSync === 'function') {
        drawerElement.animateProgressGiftSync(selectedGift ? selectedGift.level : 0);
      }

      if (shouldRefresh && ((selectedGift && !selectedGiftExists) || orderedLineUpdates.length)) {
        if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
          window.CartDrawer.refreshPromise = null;
          window.setTimeout(() => window.CartDrawer.refresh({ giftSyncHandled: true, force: true }), 0);
        }
      }
      if (giftWillChange && drawerElement && typeof drawerElement.finishGiftSyncStatus === 'function') {
        drawerElement.finishGiftSyncStatus(selectedGift && !selectedGiftExists ? 'Free gift added' : 'Free gift updated');
      }

      return rememberGiftSyncResult({
        ok: true,
        paidTotal,
        configs,
        selectedGift,
        selectedGiftExists,
        lineUpdates: orderedLineUpdates,
        addedGift: Boolean(selectedGift && !selectedGiftExists),
      });
    } catch (error) {
      // Keep storefront cart usable even if a configured gift is unavailable.
      rememberGiftSyncResult({
        ok: false,
        error: error && error.message ? error.message : String(error),
      });
      const drawerElement = getLiveCartDrawer();
      if (drawerElement && typeof drawerElement.finishGiftSyncStatus === 'function') {
        drawerElement.finishGiftSyncStatus('Gift update delayed');
      }
      if (drawerElement && typeof drawerElement.clearGiftSkeleton === 'function') {
        drawerElement.clearGiftSkeleton();
      }
      return window.__shellLastGiftSyncResult;
    } finally {
      window.__shellProgressGiftSyncing = false;
    }
  }

  window.ShellCartGiftSync = syncProgressGiftsGlobally;
  async function handleExternalCartRefresh(event) {
    const detail = (event && event.detail) || {};
    if (detail.giftSyncHandled) return;

    window.setTimeout(async () => {
      const eventCart = detail.cart && Array.isArray(detail.cart.items) ? detail.cart : null;
      const syncResult = await syncProgressGiftsGlobally({ cart: eventCart, refresh: false });
      const drawer = window.CartDrawer || document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.refresh !== 'function') return;

      drawer.refreshPromise = null;
      drawer.refresh({
        cart: giftSyncChangedGlobally(syncResult) ? null : eventCart,
        giftSyncHandled: true,
        force: true,
      });
    }, 0);
  }

  document.addEventListener('cart:updated', handleExternalCartRefresh);
  document.addEventListener('cart:refresh', handleExternalCartRefresh);
  document.addEventListener('DOMContentLoaded', () => window.setTimeout(syncProgressGiftsGlobally, 150));
  window.addEventListener('load', () => window.setTimeout(syncProgressGiftsGlobally, 150));
})();
